import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Clients, ManagementService, UsersSelection } from 'src/app/api/generated';
import {
  ConfirmationDialogComponent,
  ConfirmationDialogData,
} from 'src/app/confirmation-dialog/confirmation-dialog.component';
import { DataChangedService } from 'src/app/data-changed.service';
import {
  createImageObjectUrl,
  revokeImageObjectUrl,
  toApiImageUrl,
} from 'src/app/tools/image-url';
import { hexColorValidator } from 'src/app/tools/validators';
import { environment } from 'src/environments/environment';

import * as uuid from 'uuid';

// Creation opens the dialog without a persisted payload; edit mode receives the
// generated Clients transport model directly.
export type ClientDialogData = Partial<Clients>;

@Component({
    selector: 'app-client-dialog',
    templateUrl: './client-dialog.component.html',
    styleUrls: ['./client-dialog.component.scss'],
    standalone: false
})
export class ClientDialogComponent implements OnInit, OnDestroy {
  public clientForm: UntypedFormGroup;
  public availableUsers: UsersSelection[];
  public logoPreview: string | null = null;
  public logoFormControl = new UntypedFormControl();
  private logoObjectUrl: string | null = null;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private dialogRef: MatDialogRef<ClientDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ClientDialogData,
    private managementService: ManagementService,
    private dataChangedService: DataChangedService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.clientForm = this.formBuilder.group({
      id: [!this.data.id ? '' : this.data.id],
      name: [!this.data.name ? '' : this.data.name, [Validators.required]],
      description: [!this.data.description ? '' : this.data.description],
      primary_color: [
        !this.data.primary_color ? '' : this.data.primary_color,
        [hexColorValidator()],
      ],
      secondary_color: [
        !this.data.secondary_color ? '' : this.data.secondary_color,
        [hexColorValidator()],
      ],
      users: this.formBuilder.array([]),
      logo: this.logoFormControl,
      logoSource: [''],
    });

    this.setLogoPreview(
      toApiImageUrl(environment.apiEndpoint, this.data.logo_path),
      false
    );

    this.managementService
      .managementUsersInClientRead(
        !this.data.id ? '00000000-0000-0000-0000-000000000000' : this.data.id
      )
      .subscribe((users) => {
        this.availableUsers = users;
        this.availableUsers.forEach((user) =>
          this.usersFromFormGroup().push(new UntypedFormControl(true))
        );
      });

    // Disable name field - can only be changed via API
    this.clientForm.get('name').disable();
  }

  public onFileChange(event) {
    if (event.target.files?.length > 0) {
      const [file] = event.target.files;
      const objectUrl = createImageObjectUrl(file);
      if (objectUrl === null) {
        return;
      }

      this.setLogoPreview(objectUrl, true);
      this.clientForm.get('logoSource').patchValue(file);
    }
  }

  ngOnDestroy(): void {
    revokeImageObjectUrl(this.logoObjectUrl);
    this.logoObjectUrl = null;
  }

  public updateOrCreateClient() {
    if (!this.clientForm.valid) {
      console.error(this.clientForm.errors);
      return;
    }
    const assignedUsers = this.clientForm.value.users
      .map((enabled, index) => {
        if (enabled) {
          return this.availableUsers[index].id;
        }
      })
      .filter(Boolean);
    const assignedUsersString: Set<string> = new Set([assignedUsers.join(';')]);
    const formValue = this.clientForm.getRawValue();
    if (this.data.id) {
      this.managementService
        .managementClientUpdate(
          this.clientForm.value.id,
          formValue.name,
          assignedUsersString,
          this.clientForm.value.description,
          this.clientForm.value.logoSource,
          this.clientForm.value.primary_color,
          this.clientForm.value.secondary_color
        )
        .subscribe((success) => {
          this.dataChangedService.dataUpdated('clients');
          this.snackBar.open(
            `Auftraggeber ${formValue.name} aktualisiert.`,
            null,
            {
              duration: 1500,
            }
          );
          this.dialogRef.close();
        });
    } else {
      this.managementService
        .createTrigonosClient(
          // UUID v4,
          uuid.v4(),
          formValue.name,
          assignedUsersString,
          this.clientForm.value.description,
          this.clientForm.value.logoSource,
          this.clientForm.value.primary_color,
          this.clientForm.value.secondary_color,
          this.clientForm.value.logoSource
        )
        .subscribe((success) => {
          this.dataChangedService.dataUpdated('clients');
          this.snackBar.open(
            `Auftraggeber ${formValue.name} erstellt.`,
            null,
            {
              duration: 1500,
            }
          );
          this.dialogRef.close();
        });
    }
  }

  public deleteClient(event: MouseEvent) {
    if (!this.data.id) {
      return;
    }

    // Confirm deletion
    const deleteDialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: <ConfirmationDialogData>{
        headlineText: `Auftraggeber ${this.data.name} endgültig entfernen? `,
        confirmationButtonText: 'Ja',
        rejectButtonText: 'Nein',
        displayText: `ACHTUNG: Es werden ALLE Projekte und Reports des Auftraggebers gelöscht.`,
      },
    });
    deleteDialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.managementService
          .managementClientDelete(this.data.id)
          .subscribe((res) => {
            this.dataChangedService.dataUpdated('clients');
          });
      }
    });
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  private usersFromFormGroup(): UntypedFormArray {
    return this.clientForm.get('users') as UntypedFormArray;
  }

  private setLogoPreview(value: string | null, isObjectUrl: boolean): void {
    revokeImageObjectUrl(this.logoObjectUrl);
    this.logoObjectUrl = isObjectUrl ? value : null;
    this.logoPreview = value;
  }
}
